import { createFileRoute, redirect } from '@tanstack/react-router'
import { z } from 'zod'
import { useState } from 'react'

import { LoginForm } from '@/features/authentication/components/login-form'

const fallback = '/dashboard' as const

export const Route = createFileRoute('/login')({
  validateSearch: z.object({
    redirect: z.string().optional().catch(''),
  }),
  beforeLoad: ({ context, search }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: search.redirect || fallback })
    }
  },
  component: Login,
})

function Login() {
  const [showLoginForm, setShowLoginForm] = useState(false)

  return (
    <div className="relative isolate flex min-h-screen flex-col overflow-hidden bg-gradient-to-br from-slate-900 via-primary/95 to-slate-950 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <main className="relative z-10 grid flex-1 grid-cols-1 text-accent dark:text-accent lg:grid-cols-[1.05fr_0.95fr]">
        <section className="relative hidden flex-col justify-center gap-10 bg-gradient-to-br from-primary via-primary/95 to-slate-900 bg-cover bg-center bg-no-repeat px-12 py-16 text-white lg:flex xl:px-20 overflow-hidden" style={{ backgroundImage: 'url(/images/certificate-bg.png)' }}>
          {/* Background overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/85 to-slate-900/80" />
          {/* Floating geometric shapes */}
          <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full border border-white/20 animate-float-slowest" />
          <div className="absolute top-1/4 -right-10 w-32 h-32 rounded-lg border border-white/15 rotate-45 animate-float-slower" />
          <div className="absolute bottom-20 -left-16 w-36 h-36 rounded-full border border-white/10 animate-float-slow" />

          <div className="flex items-center gap-4 relative z-10">
            <img src="/images/logo/logo-light.png" alt="UIBFS" className="h-12 w-auto drop-shadow" />
            <div className="text-sm font-semibold uppercase tracking-[0.28em] text-white/80">Membership</div>
          </div>

          <div className="space-y-5 relative z-10">
            <h1 className="text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-[3.4rem] text-white">Join UIBFS</h1>
            <p className="max-w-xl text-lg leading-relaxed text-white/85">
              Become part of a thriving community of finance professionals. Access exclusive resources, networking opportunities, and professional development programs.
            </p>
          </div>

          <div className="space-y-4 relative z-10">
            {[
              'Expert-led professional development',
              'Exclusive networking events',
              'Industry certifications',
              'Career advancement opportunities',
            ].map((benefit) => (
              <div key={benefit} className="flex items-start gap-3">
                <div className="h-2 w-2 rounded-full bg-white flex-shrink-0 mt-2" />
                <p className="text-base text-white/85">{benefit}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="relative flex items-center justify-center bg-white/85 px-6 py-14 backdrop-blur-xl dark:bg-slate-950/70 lg:px-12 xl:px-18">
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/50 to-white/40 dark:from-slate-950/50 dark:via-slate-950/55 dark:to-slate-950/60" aria-hidden />
          <div className="relative z-10 w-full max-w-lg">
            <div className="mb-8 flex items-center gap-3 lg:hidden">
              <img src="/images/logo/logo.png" alt="UIBFS" className="h-10 w-auto" />
              <span className="text-xs font-semibold uppercase tracking-[0.28em] text-primary dark:text-accent">Membership</span>
            </div>

            {!showLoginForm ? (
              <div className="space-y-4 text-center">
                <div className="space-y-2 mb-8">
                  <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Welcome</h2>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Already a member? Sign in to your account
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => setShowLoginForm(true)}
                    className="w-full h-11 rounded-lg bg-gradient-to-r from-primary to-[color-mix(in_oklab,var(--primary),white_18%)] text-primary-foreground font-semibold shadow-lg hover:from-[color-mix(in_oklab,var(--primary),white_8%)] hover:to-[color-mix(in_oklab,var(--primary),white_28%)] focus-visible:ring-2 focus-visible:ring-primary transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <span>Sign In</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>

                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-slate-300 dark:bg-slate-600" />
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400 p-8">Not a member?</span>
                    <div className="flex-1 h-px bg-slate-300 dark:bg-slate-600" />
                  </div>

                  <a
                    href="/membership/apply"
                    className="w-full h-11 rounded-lg bg-gradient-to-r from-primary to-[color-mix(in_oklab,var(--primary),white_18%)] text-primary-foreground font-semibold shadow-lg hover:from-[color-mix(in_oklab,var(--primary),white_8%)] hover:to-[color-mix(in_oklab,var(--primary),white_28%)] focus-visible:ring-2 focus-visible:ring-primary transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <span>Apply for membership</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </a>
                </div>
              </div>
            ) : (
              <div
                className={`transition-all duration-500 ease-out ${
                  showLoginForm ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-[0.98]'
                }`}
              >
                <button
                  onClick={() => setShowLoginForm(false)}
                  className="mb-4 flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
                <LoginForm />
              </div>
            )}
          </div>
        </section>
      </main>

      <footer className="pointer-events-none absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-center text-xs text-slate-300 dark:text-slate-400">
        <p className="pointer-events-auto">© 2025 UIBFS. All rights reserved.</p>
      </footer>
    </div>
  )
}
